import { useEffect } from 'react'

// Switch the map projection (globe ⇆ mercator) when the toggle changes: set the
// MapLibre projection, swap the GeoJSON source between the clamped (mercator) and
// unclamped (globe) datasets, toggle renderWorldCopies, and swap the container
// background (galaxy image for globe). Notifies the parent of the active dataset.
export function useProjection(map, mapContainer, { isGlobeView, regionsMercatorData, regionsGlobeData, onRegionsDataLoaded, setRegionsData, mapBackground }) {
  useEffect(() => {
    if (!map.current || !regionsMercatorData || !regionsGlobeData) return

    const updateProjection = () => {
      try {
        console.log('🔄 Switching projection to:', isGlobeView ? 'Globe' : 'Mercator')

        // Always set projection directly (MapLibre handles it gracefully)
        map.current.setProjection({
          type: isGlobeView ? 'globe' : 'mercator'
        })

        // Switch GeoJSON data source between mercator and globe versions
        const source = map.current.getSource('regions')
        if (source) {
          const newData = isGlobeView ? regionsGlobeData : regionsMercatorData
          // Extract clean data without _areaIndex (Map objects can't be serialized)
          const cleanData = {
            type: newData.type,
            features: newData.features
          }
          source.setData(cleanData)
          setRegionsData(newData) // Update active regions data for other components
          console.log('✅ GeoJSON data switched to:', isGlobeView ? 'Globe (no clamp)' : 'Mercator (±85° clamp)')

          // Notify parent of data change
          if (onRegionsDataLoaded) {
            onRegionsDataLoaded(newData)
          }
        }

        // Update renderWorldCopies for plane mode
        if (!isGlobeView) {
          map.current.setRenderWorldCopies(true)
        } else {
          map.current.setRenderWorldCopies(false)
        }

        // Update background color and container background
        const container = mapContainer.current
        if (isGlobeView) {
          // Dark grey background for globe + galaxy image
          map.current.setPaintProperty('background', 'background-color', mapBackground || '#1a1a1a')
          container.style.backgroundImage = 'url(/galaxy.jpeg)'
          container.style.backgroundSize = 'cover'
          container.style.backgroundPosition = 'center'
        } else {
          // Ocean blue for flat projection
          map.current.setPaintProperty('background', 'background-color', mapBackground || '#1a1a1a')
          container.style.backgroundImage = 'none'
        }
      } catch (error) {
        console.error('Error changing projection:', error)
      }
    }

    // Apply immediately with a small delay to ensure map is ready
    const timeout = setTimeout(() => {
      updateProjection()
    }, 100)

    return () => clearTimeout(timeout)
  }, [map, mapContainer, isGlobeView, regionsMercatorData, regionsGlobeData, onRegionsDataLoaded, setRegionsData, mapBackground])
}
